import { cn, Button } from "@tellescope/design-system";
import { BlockProps } from "../../../types";
import { useAISummaryBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { AISummaryCard } from "./AISummaryCard";
import { AISummaryEmpty } from "./AISummaryEmpty";
import { AISummaryEmptyPreview } from "./AISummaryEmptyPreview";
import { AISummaryGenerating } from "./AISummaryGenerating";
import { AISummaryFocusField } from "./AISummaryFocusField";
import { AISummaryLoading } from "./AISummaryLoading";

export const AISummaryBlock = ({
  enduserId,
  info,
  className,
  viewId,
  blockId,
  blockIndex,
  onMaximize,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  alwaysShowButtons = false,
  contentClassName,
}: BlockProps) => {
  const {
    t,
    parsedInfo,
    summary,
    summaryDate,
    isGenerating,
    isSummaryLoading,
    canGenerateNewSummary,
    generateSummary,
    focus,
    setFocus,
    saveFocus,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
  } = useAISummaryBlock({
    enduserId,
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    viewId,
    blockId,
    blockIndex,
  });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col gap-3", contentClassName)}
      >
        {summary ? (
          <>
            <AISummaryCard content={summary} date={summaryDate} />
            {editMode && (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="xs"
                  color="muted"
                  onClick={() => generateSummary()}
                  disabled={!canGenerateNewSummary || isGenerating}
                  loading={isGenerating}
                >
                  {t("Regenerate")}
                </Button>
              </div>
            )}
          </>
        ) : isGenerating ? (
          <AISummaryGenerating />
        ) : isSummaryLoading ? (
          <AISummaryLoading />
        ) : editMode ? (
          <AISummaryEmpty
            onGenerate={() => generateSummary()}
            isGenerating={isGenerating}
            disabled={!canGenerateNewSummary}
          />
        ) : (
          <AISummaryEmptyPreview />
        )}

        {editMode && <AISummaryFocusField value={focus} onChange={setFocus} onBlur={saveFocus} />}
      </BlockContent>
    </div>
  );
};
